import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Import icons
import { FiCheck } from 'react-icons/fi';
import { FiCalendar } from 'react-icons/fi';
import { FiMail } from 'react-icons/fi';
import { FiUsers } from 'react-icons/fi';
import { FiArrowRight } from 'react-icons/fi';
import { FiX } from 'react-icons/fi';
import { FiClock } from 'react-icons/fi';
import { FiAlertCircle } from 'react-icons/fi';
import { FiBriefcase } from 'react-icons/fi';
import { FiCreditCard } from 'react-icons/fi';

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 100 }
  },
};

const importantItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    scale: [1, 1.02, 1],
    transition: {
      type: 'spring',
      stiffness: 100,
      scale: {
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut',
      },
    },
  },
};

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [nextWorkshopDate, setNextWorkshopDate] = useState('');

  const getNextSaturdayWorkshop = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 (Sun) to 6 (Sat)
    const currentHour = now.getHours();
    
    let nextSaturday = new Date();
    
    // If today is Saturday and it's before 8:00 PM (20:00)
    if (currentDay === 6 && currentHour < 20) {
      // Today is the workshop day
    } 
    else {
      // Calculate days until next Saturday
      // If today is Sat (6) and after 8 PM, we want next Sat (7 days away)
      const daysUntilNextSaturday = currentDay === 6 ? 7 : (6 - currentDay + 7) % 7;
      nextSaturday.setDate(now.getDate() + daysUntilNextSaturday);
    }
    
    nextSaturday.setHours(20, 0, 0, 0); // 8:00 PM
    return nextSaturday;
  };

  const formatWorkshopDate = (date) => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getTimeUntilWorkshop = (workshopDate) => {
    const now = new Date();
    const timeDiff = workshopDate.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return "Workshop is starting now!";
    }
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''} to go!`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''} to go!`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} to go!`;
    }
  };

  const handleWhatsAppClick = () => {
    window.open('https://chat.whatsapp.com/GWMhCNCoUGhFuBkqFUUMhL', '_blank');
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    const updateWorkshopDate = () => {
      const nextWorkshop = getNextSaturdayWorkshop();
      setNextWorkshopDate(formatWorkshopDate(nextWorkshop));
    };

    updateWorkshopDate();
    const interval = setInterval(updateWorkshopDate, 60000);

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  const nextWorkshop = getNextSaturdayWorkshop();
  const timeUntilWorkshop = getTimeUntilWorkshop(nextWorkshop);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 font-['Inter']">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">10XAI Founder Workshop</h1>
            <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold border border-green-200">
              Seat Confirmed
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Main Congratulations Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="bg-white border-2 border-green-500/20 rounded-3xl p-8 mb-8 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-green-500"></div>
            
            <div className="flex justify-center mb-4">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 15, delay: 0.2 }}
                className="bg-green-500 rounded-full p-4 shadow-lg shadow-green-200"
              >
                <FiCheck className="text-4xl text-white" />
              </motion.div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Registration Successful! 🎉
            </h1>
            
            <div className="flex flex-col items-center gap-3 mb-6">
              <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-xl border border-emerald-100 font-bold flex items-center shadow-sm">
                <FiCreditCard className="mr-2" />
                Fee Paid: ₹49
              </div>
              
              <div className="bg-blue-50 text-blue-700 px-6 py-3 rounded-2xl border border-blue-100 font-semibold flex items-center shadow-sm max-w-md">
                <FiBriefcase className="mr-3 text-xl flex-shrink-0" />
                <span>A warm welcome to all Women Life Coach joining us! 💼</span>
              </div>
            </div>

            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              You've successfully secured your spot for the upcoming live session. Get ready to learn how to automate your professional workflows and reclaim 20+ hours a week! 🚀
            </p>
          </div>
        </motion.div>

        {/* Next Steps Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100"
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center flex items-center justify-center">
            <span className="bg-gray-100 p-2 rounded-lg mr-3">📋</span>
            Essential Next Steps
          </h3>
          
          <div className="space-y-6">
            {/* Step 1: WhatsApp - HIGHEST PRIORITY */}
            <motion.div variants={importantItemVariants} className="flex items-start space-x-4 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border-2 border-green-500 shadow-md">
              <div className="bg-green-600 text-white rounded-full w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold shadow-lg text-lg">1</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 flex items-center">
                  <FiUsers className="mr-2 text-green-600" />
                  Join the Professional Community 🔥
                </h4>
                <p className="text-gray-700 mt-2 font-medium">
                  This is mandatory! The LIVE session link and exclusive AI checklists are shared <span className="text-green-700 font-bold">only</span> inside the WhatsApp group.
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleWhatsAppClick}
                  className="mt-5 bg-green-600 text-white px-8 py-4 rounded-xl font-extrabold flex items-center justify-center space-x-3 shadow-xl hover:bg-green-700 transition-all w-full md:w-auto"
                >
                  <FiUsers className="text-xl" />
                  <span>Join WhatsApp Community Now</span>
                  <FiArrowRight />
                </motion.button>
              </div>
            </motion.div>

            {/* Step 2: Calendar */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4 p-6 bg-purple-50 rounded-2xl border border-purple-100">
              <div className="bg-purple-500 text-white rounded-full w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold shadow-md text-lg">2</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 flex items-center">
                  <FiCalendar className="mr-2 text-purple-600" />
                  Mark Your Calendar 📅
                </h4>
                <div className="mt-4 p-5 bg-white rounded-xl border border-purple-200 shadow-sm">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="text-purple-900 font-bold text-xl mb-1">
                        Every Saturday
                      </p>
                      <p className="text-purple-700 font-semibold text-lg">
                        {nextWorkshopDate}
                      </p>
                    </div>
                    <div className="bg-purple-100 px-4 py-2 rounded-lg">
                      <p className="text-purple-800 font-extrabold text-xl">
                        8:00 PM IST
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-purple-600 font-bold bg-purple-50/50 p-2 rounded-lg border border-purple-100 inline-flex">
                    <FiClock className="mr-2" />
                    <span>{timeUntilWorkshop}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 3: Email */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4 p-6 bg-blue-50 rounded-2xl border border-blue-100">
              <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold shadow-md text-lg">3</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 flex items-center">
                  <FiMail className="mr-2 text-blue-600" />
                  Check Your Confirmation Email 📧
                </h4>
                <p className="text-gray-600 mt-2">
                  We've sent your payment receipt (₹49) and session details to your inbox.
                </p>
                <div className="mt-4 flex items-start space-x-3 text-sm bg-white p-4 rounded-xl border border-blue-200 shadow-sm">
                  <FiAlertCircle className="text-blue-600 mt-0.5 flex-shrink-0 text-lg" />
                  <p className="text-blue-800 font-medium leading-relaxed">
                    Check your <span className="font-bold">Spam/Promotions</span> folder if you don't see it. Mark us as "Not Spam" to ensure you get the final reminders!
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Urgency Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gray-900 rounded-3xl shadow-2xl p-10 text-white mb-16 relative overflow-hidden"
        >
          <div className="relative z-10 text-center">
            <h3 className="text-3xl md:text-4xl font-extrabold mb-6">
              Don't Miss the Live Link! ⚠️
            </h3>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              We start sharp at 8:00 PM. The private Zoom link is shared <span className="text-white font-bold">exclusively</span> inside the community 15 minutes before the start.
            </p>
            
            <motion.button
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleWhatsAppClick}
              className="bg-green-500 text-white font-black text-xl px-12 py-6 rounded-2xl shadow-2xl flex items-center justify-center mx-auto space-x-4 hover:bg-green-400 transition-colors"
            >
              <FiUsers className="text-2xl" />
              <span>JOIN THE WHATSAPP GROUP</span>
              <FiArrowRight className="text-2xl" />
            </motion.button>
          </div>
          
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-green-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="border-t border-gray-200 pt-10 mt-12 text-center text-gray-500"
        >
          <p className="mb-4 text-lg">
            Questions? We're here to help:
            <a href="mailto:support@yinguru.com" className="text-blue-600 font-bold hover:underline ml-2">
              support@yinguru.com
            </a>
          </p>
          <div className="flex justify-center gap-6 text-sm font-medium text-gray-400 mb-6">
            <span>Privacy Policy</span>
            <span>Terms of Service</span>
            <span>Refund Policy</span>
          </div>
          <p className="text-sm">
            © 2026 10XAI Founder Workshop. All rights reserved. Empowering professionals through AI automation.
          </p>
        </motion.div>
      </div>

      {/* Persistence Popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full p-10 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-3 bg-green-500"></div>
              
              <button
                onClick={closePopup}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
              >
                <FiX size={28} />
              </button>
              
              <div className="text-center">
                <div className="bg-green-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-inner border border-green-100">
                  <FiUsers className="text-5xl text-green-600" />
                </div>
                
                <h3 className="text-3xl font-black text-gray-900 mb-4">
                  Final Step! 🚀
                </h3>
                
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  To get the <strong>Live Zoom Link</strong> and your <strong>AI Bonuses</strong>, you must join the WhatsApp community.
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    handleWhatsAppClick();
                    closePopup();
                  }}
                  className="bg-green-600 text-white font-black text-xl px-8 py-5 rounded-2xl w-full shadow-xl hover:bg-green-700 transition-all flex items-center justify-center space-x-3"
                >
                  <FiUsers />
                  <span>Join Community Now</span>
                </motion.button>
                
                <button
                  onClick={closePopup}
                  className="text-gray-400 hover:text-gray-600 mt-6 text-sm font-bold transition-colors underline decoration-gray-300 underline-offset-4"
                >
                  I'll join later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;